import { useQuery } from '@tanstack/react-query';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth.context';

interface PlayerRating {
  date: string;
  rating: number;
}

interface Competitor {
  name: string;
  history: number[];
}

export interface PlayerStats {
  ratings: {
    history: PlayerRating[];
    competitors: Competitor[];
  };
  skills: {
    creativity: number;
    attitude: number;
    technical: number;
    tactical: number;
  };
  performance: {
    pace: number;
    physicality: number;
    passing: number;
    dribbling: number;
    defending: number;
    shooting: number;
  };
  heatmap: number[][];
  debut: string;
  history: {
    club: string;
    period: string;
    appearances: number;
  }[];
  preferredFoot: string;
  form: number;
  physicalAttributes: {
    height: number;
    weight: number;
    bodyFat: number;
    stamina: number;
    strength: number;
  };
}

export interface PlayerDetails {
  id: string;
  name: string;
  position: string;
  club_name: string;
  image_url?: string;
  nationality?: string;
  age?: number;
  market_value?: string;
  foot?: string;
  height?: string;
}

// Original hook for detailed player stats
export const usePlayerDetails = (playerId: string | null) => {
  return useQuery<PlayerStats>({
    queryKey: ['playerDetails', playerId],
    queryFn: async () => {
      if (!playerId) throw new Error('No player selected');
      const response = await fetch(`/api/v1/player/${playerId}/stats`);
      if (!response.ok) {
        throw new Error('Failed to fetch player details');
      }
      return response.json();
    },
    enabled: !!playerId,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    gcTime: 0 // Don't cache the data
  });
};

/**
 * Hook to fetch basic profile details for a single player
 * @param playerId The ID of the player to fetch
 * @returns Query result with player details
 */
export const usePlayerProfile = (playerId: string | null) => {
  const api = useApi();
  const { getToken } = useAuth();

  return useQuery({
    queryKey: ['playerProfile', playerId],
    queryFn: async () => {
      if (!playerId) {
        throw new Error('Player ID is required');
      }

      const token = getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      const response = await fetch(`${baseUrl}/ai/v1/player/${playerId}/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch player details: ${response.status}`);
      }

      return await response.json() as PlayerDetails;
    },
    enabled: !!playerId && !!getToken(),
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch details for multiple players in a batch
 * @param playerIds Array of player IDs to fetch
 * @returns Query result with player details mapped by ID
 */


/**
 * Hook to fetch the current season stats for a player based on:
 * a) The player's current club
 * b) The main league that club is in
 * c) The latest season for the player in that league
 * 
 * @param playerId The ID of the player to fetch stats for
 * @returns Query result with the player's current season stats
 */
export const usePlayerCurrentSeasonStats = (playerId: string | null) => {
  const api = useApi();
  const { getToken } = useAuth();

  return useQuery({
    queryKey: ['playerCurrentSeasonStats', playerId],
    queryFn: async () => {
      if (!playerId) {
        throw new Error('Player ID is required');
      }

      const token = getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      const response = await fetch(`${baseUrl}/ai/v1/player/${playerId}/current-season-stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.status === 404) {
        // No stats found is a valid response
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch current season stats: ${response.status}`);
      }

      return await response.json();
    },
    enabled: !!playerId && !!getToken(),
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useBatchPlayerDetails = (playerIds: string[]) => {
  const api = useApi();
  const { getToken } = useAuth();

  return useQuery({
    queryKey: ['players', 'batch', playerIds],
    queryFn: async () => {
      if (!playerIds.length) {
        return {};
      }

      console.log(`PERF:useBatchPlayerDetails: Starting fetch for ${playerIds.length} players:`, playerIds);
      
      const token = getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Convert array of IDs to comma-separated string
      const idString = playerIds.join(',');

      // Record the start time for the entire operation
      const startTime = performance.now();
      console.time('PERF:useBatchPlayerDetails:fetch-batch-players');
      
      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      
      // Log if there's an ongoing KYP search
      const kypSearchElement = document.querySelector('[data-kyp-search-active="true"]');
      if (kypSearchElement) {
        console.log('PERF:useBatchPlayerDetails: KYP search is active during player details fetch');
      }
      
      // Set parameters for wishlist loading to ensure faster response
      // priority=true tells the backend to prioritize this request
      // minimal=true tells the backend to only fetch the minimal data needed for display
      const params = '&priority=true&minimal=true';
      
      const response = await fetch(`${baseUrl}/ai/v1/players/batch?player_ids=${idString}${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      console.timeEnd('PERF:useBatchPlayerDetails:fetch-batch-players');
      
      // Log how long the network request took
      console.log(`PERF:useBatchPlayerDetails: Network request took ${(performance.now() - startTime).toFixed(2)}ms`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Batch player details error response:', errorText);
        throw new Error(`Failed to fetch player details: ${response.status} - ${errorText}`);
      }

      console.time('parse-batch-response');
      const players = await response.json() as PlayerDetails[];
      console.timeEnd('parse-batch-response');
      
      console.log(`Received ${players.length} players from batch API`);
      
      // Convert array to map for easier lookup
      const playerMap: Record<string, PlayerDetails> = {};
      players.forEach(player => {
        playerMap[player.id] = player;
      });

      // Check if any players are missing
      const missingPlayerIds = playerIds.filter(id => !playerMap[id]);
      if (missingPlayerIds.length > 0) {
        console.warn('Missing player details for IDs:', missingPlayerIds);
      }

      return playerMap;
    },
    enabled: playerIds.length > 0 && !!getToken(),
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Add a longer cache time to prevent garbage collection too soon
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to fetch the availability timeline data for a player
 * @param playerId The ID of the player to fetch data for
 * @returns Query result with the player's availability timeline
 */
export interface PlayerAvailabilityTimelineEntry {
  season: string;
  club: {
    id: string;
    name: string;
  };
  league: {
    id: string;
    name: string;
  };
  minutes_played: number;
  appearances: number;
  games_missed_injury: number;
  bradford_factor: number;
  injuries: {
    player_id: string;
    season: string;
    injury: string;
    from_date: string;
    until_date: string | null;
    days: number;
    games_missed: number;
    updated_at: string;
  }[];
}

export interface PlayerAvailabilityTimelineData {
  timeline: PlayerAvailabilityTimelineEntry[];
  average_bradford_factor: number;
}

export const usePlayerAvailabilityTimeline = (playerId: string | null) => {
  const api = useApi();
  const { getToken } = useAuth();

  return useQuery<PlayerAvailabilityTimelineData>({
    queryKey: ['playerAvailabilityTimeline', playerId],
    queryFn: async () => {
      if (!playerId) {
        throw new Error('Player ID is required');
      }

      const token = getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      const response = await fetch(`${baseUrl}/ai/v1/player/${playerId}/availability-timeline`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch availability timeline: ${response.status}`);
      }

      return await response.json();
    },
    enabled: !!playerId && !!getToken(),
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Interface for market value momentum data
 */
export interface MarketValueMomentumData {
  season: string;
  market_value: number | null;
  pes_score: number | null;
  league_name: string;
  club_name: string;
}

/**
 * Hook to fetch market value momentum data for a player
 * @param playerId The ID of the player to fetch data for
 * @returns Query result with the player's market value and PES score by season
 */
export const usePlayerMarketValueMomentum = (playerId: string | null) => {
  const api = useApi();
  const { getToken } = useAuth();

  return useQuery<MarketValueMomentumData[]>({
    queryKey: ['playerMarketValueMomentum', playerId],
    queryFn: async () => {
      if (!playerId) {
        throw new Error('Player ID is required');
      }

      const token = getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      const response = await fetch(`${baseUrl}/ai/v1/player/${playerId}/market-value-momentum`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch market value momentum data: ${response.status}`);
      }

      return await response.json();
    },
    enabled: !!playerId && !!getToken(),
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Interface for club league information
 */
export interface ClubLeagueInfo {
  club_id: string;
  club_name: string;
  league_id: string;
  league_name: string;
  country_name: string;
  country_code: string;
}

/**
 * Hook to fetch club league information for a player's current club
 * @param playerId The ID of the player to fetch data for
 * @returns Query result with the player's club league information
 */
export const usePlayerClubLeagueInfo = (playerId: string | null) => {
  const api = useApi();
  const { getToken } = useAuth();

  return useQuery<ClubLeagueInfo>({
    queryKey: ['playerClubLeagueInfo', playerId],
    queryFn: async () => {
      if (!playerId) {
        throw new Error('Player ID is required');
      }

      const token = getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      const response = await fetch(`${baseUrl}/ai/v1/player/${playerId}/club-league-info`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.status === 404) {
        // No club league info found is a valid response
        return {
          club_id: '',
          club_name: '',
          league_id: '',
          league_name: '',
          country_name: '',
          country_code: ''
        };
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch club league info: ${response.status}`);
      }

      return await response.json();
    },
    enabled: !!playerId && !!getToken(),
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};